import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl, OG_VERSION } from "../components/Seo";
import { domainOf, timeAgo, outboundHref } from "../lib/utils-tv";
import { API, recordLaunch } from "../lib/api";
import { submitVendorLead } from "../lib/api";
import { shareUrl } from "../lib/shareUrl";
import RelatedTools from "../components/RelatedTools";
import BadgeEmbed from "../components/BadgeEmbed";
import VendorSpotlight, { ClaimLine } from "../components/VendorSections";
import { useVendorFlags } from "../lib/useVendorFlags";
import ToolCTA from "../components/ToolCTA";
import PromptBlock from "../components/PromptBlock";
import AddToTerminalButton from "../components/AddToTerminalButton";

export default function ToolDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { flags: vendorFlags } = useVendorFlags();
  const [tool, setTool] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoLabel, setDemoLabel] = useState("Request a demo");

  // Merged duplicate slug → swap the URL to the surviving tool (canonical).
  useEffect(() => {
    if (tool?.redirected_from && tool.slug && tool.slug !== slug) {
      nav(`/tool/${tool.slug}`, { replace: true });
    }
  }, [tool, slug, nav]);

  useEffect(() => {
    let alive = true;
    // Deferred to a microtask so setStatus isn't a synchronous state update in
    // the effect body.
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus("loading");
      axios
        .get(`${API}/tools/by-slug/${encodeURIComponent(slug)}`)
        .then((r) => {
          if (!alive) return;
          setTool(r.data);
          setStatus("ok");
        })
        .catch((err) => {
          if (!alive) return;
          if (err?.response?.status === 404) setStatus("notfound");
          else setStatus("error");
        });
    });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div data-testid="tool-detail-loading">
        <Seo
          title="Loading…"
          description="Loading tool details."
          path={`/tool/${slug}`}
          noindex
        />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <div className="text-[13px] text-[#888]">
            loading tool...<span className="tv-blink">_</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div data-testid="tool-detail-404">
        <Seo
          title="Tool not found"
          description="That tool isn't in the index."
          path={`/tool/${slug}`}
          noindex
        />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold border-b border-black pb-2">
            {">"}_ 404 / no such tool
          </h1>
          <p className="mt-4 text-[13px] text-[#444]">
            That slug isn&apos;t in the index. It may have been removed or
            renamed.
          </p>
          <p className="mt-4">
            <Link to="/" className="tv-btn">
              [← back to index]
            </Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "error" || !tool) {
    return (
      <div data-testid="tool-detail-error">
        <Seo title="Error" path={`/tool/${slug}`} noindex />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold">{">"}_ error</h1>
          <p className="text-[13px] text-[#444] mt-2">
            could not load tool. try again.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const price = tool.pricing || "$?";
  const path = `/tool/${tool.slug}`;
  const url = siteUrl(path);
  // SEO title/description come from the backend (seo_meta.tool_seo) so Helmet,
  // the prerender and the share route all emit the exact same string.
  const seoTitle =
    tool.seo?.title ||
    (tool.asset_class && tool.category
      ? `${tool.title} — ${tool.category} for ${tool.asset_class}`
      : tool.category
      ? `${tool.title} — ${tool.category}`
      : tool.title);
  const seoDesc = tool.seo?.description || `${tool.title} — ${tool.description}`.slice(0, 320);

  // SoftwareApplication schema for Google rich results.
  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: tool.title,
    url: tool.url,
    description: tool.description,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: price === "FREE" || price.startsWith("FREE") ? "0" : undefined,
      priceCurrency: "USD",
      category:
        price === "FREE"
          ? "Free"
          : price.includes("CALL")
          ? "Contact for pricing"
          : "Paid",
    },
    aggregateRating:
      tool.upvotes && tool.upvotes > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: "5",
            ratingCount: String(tool.upvotes),
            bestRating: "5",
            worstRating: "1",
          }
        : undefined,
  };

  // Breadcrumb schema for navigation context.
  const crumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Index",
        item: siteUrl("/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: tool.category,
        item: siteUrl(`/f/${slugifyClient(tool.category)}`),
      },
      {
        "@type": "ListItem",
        position: 3,
        name: tool.title,
        item: url,
      },
    ],
  };

  // A claimed Starter listing with maker-provided content → render the premium
  // "maker-first" layout (spotlight + prominent CTA high on the page).
  const hasVendor = !!(
    tool.vendor &&
    (tool.vendor.positioning ||
      (tool.vendor.screenshots && tool.vendor.screenshots.length) ||
      tool.vendor.cta_url ||
      (tool.vendor.ctas && tool.vendor.ctas.length) ||
      tool.vendor.video_url ||
      (tool.vendor.integrations && tool.vendor.integrations.length))
  );

  const isFormCta = (c) => c && (c.type === "demo" || c.type === "meeting");
  const vendorCtas = hasVendor
    ? (tool.vendor.ctas && tool.vendor.ctas.length
        ? tool.vendor.ctas
        : (tool.vendor.cta_url ? [{ label: tool.vendor.cta_label, url: tool.vendor.cta_url }] : []))
    : [];
  const primaryCta = vendorCtas[0] || null;
  const openDemo = (c) => {
    setDemoLabel(c?.label || "Request a demo");
    setDemoOpen(true);
  };

  return (
    <div data-testid="tool-detail-page">
      <Seo
        title={seoTitle}
        bare
        description={seoDesc}
        path={path}
        image={siteUrl(`/api/og/tool/${tool.slug}.png?v=${OG_VERSION}`)}
        ogType="article"
        noindex={!!tool.example}
        jsonLd={[softwareLd, crumbLd]}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-8">
        {tool.example && (
          <div className="mb-4 border border-black bg-[#FBF8EE] p-3 text-[12px] flex flex-col sm:flex-row sm:items-center gap-2" data-testid="tool-example-banner">
            <span className="flex-1">
              <b className="uppercase tracking-wide">example claimed page</b> — a preview of what a claimed
              Starter listing looks like. This page is private: it isn&apos;t in the index, search or sitemap.
            </span>
            <a href="/for-vendors" className="tv-darklink bg-black text-white border border-black px-3 py-1.5 font-bold no-underline hover:bg-[#222] whitespace-nowrap" data-testid="tool-example-claim-cta">
              Claim a page like this →
            </a>
          </div>
        )}
        {/* Breadcrumb / back */}
        <div className="text-[12px] text-[#888] mb-3" data-testid="breadcrumbs">
          <Link to="/" className="tv-link">
            index
          </Link>{" "}
          /{" "}
          <Link
            to={`/f/${slugifyClient(tool.category)}`}
            className="tv-link"
          >
            {tool.category}
          </Link>{" "}
          / <span className="text-black">{tool.title}</span>
        </div>

        <h1
          className="text-xl font-bold border-b border-black pb-2 flex items-center gap-2 flex-wrap"
          data-testid="tool-detail-title"
        >
          {hasVendor && tool.vendor?.logo && (
            <img
              src={tool.vendor.logo}
              alt={`${tool.title} logo`}
              className="h-8 w-8 object-contain border border-black bg-white p-1 shrink-0"
              data-testid="tool-detail-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <span>{">"}_ {tool.title}</span>
          {tool.claimed && (
            <span className="text-[10px] font-bold uppercase tracking-wide border border-black px-1.5 py-0.5 align-middle" title="claimed by the company that builds it" data-testid="tool-claimed-mark">
              ✓ claimed
            </span>
          )}
        </h1>

        {vendorFlags.claiming && <ClaimLine tool={tool} />}

        {tool.is_terminal && (
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="tool-detail-terminal">
            <Link
              to="/?terminals=1"
              className="text-[10px] font-bold uppercase tracking-wide border border-black px-1.5 py-0.5 leading-none no-underline hover:bg-black hover:text-white"
              title="Multi-function terminal — browse all terminals"
            >
              terminal
            </Link>
            {Array.isArray(tool.terminal_types) && tool.terminal_types.length > 0 && (
              <span className="text-[12px] text-[#666]">
                {tool.terminal_types.map((ft, i) => (
                  <span key={ft}>
                    <Link
                      to={`/?terminals=1&terminal_type=${encodeURIComponent(ft)}`}
                      className="tv-link"
                      data-testid={`tool-detail-terminal-type-${i}`}
                    >
                      {ft}
                    </Link>
                    {i < tool.terminal_types.length - 1 && ", "}
                  </span>
                ))}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 flex items-baseline gap-3 flex-wrap text-[12px] text-[#666]">
          {tool.prompt_text ? (
            <span className="text-[14px] font-bold text-black" data-testid="tool-detail-prompt-label">
              [hosted prompt — copy / launch below]
            </span>
          ) : (
            <>
              <a
                href={outboundHref(tool, "tool")}
                target="_blank"
                rel="sponsored noopener noreferrer"
                className="tv-link text-[14px] font-bold"
                data-testid="tool-detail-url"
              >
                {tool.url} ↗
              </a>
              <span>({domainOf(tool.url)})</span>
            </>
          )}
          <Link
            to="/about#pricing"
            className="ml-auto text-[12px] font-bold text-[#000] tabular-nums whitespace-nowrap hover:bg-black hover:text-white no-underline"
            title={`Pricing: ${price} — click for tier legend`}
            data-testid="tool-detail-price"
          >
            {price}
          </Link>
        </div>

        {hasVendor && tool.description && (
          <p className="mt-3 text-[14px] text-[#222] leading-relaxed" data-testid="tool-detail-tagline">
            {tool.description}
          </p>
        )}

        {hasVendor && (tool.upvotes > 0 || (tool.category_rank && tool.category_rank <= 25) || (tool.pricing || "").toUpperCase().startsWith("FREE")) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="tool-detail-trust">
            {tool.upvotes > 0 && <span className="border border-black px-1.5 py-0.5">▲ {tool.upvotes} upvotes</span>}
            {tool.category_rank && tool.category_rank <= 25 && (
              <span className="border border-black px-1.5 py-0.5">#{tool.category_rank} in {tool.category}</span>
            )}
            {(tool.pricing || "").toUpperCase().startsWith("FREE") && <span className="border border-black px-1.5 py-0.5">✓ free tier</span>}
          </div>
        )}

        {hasVendor && vendorCtas.length > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2" data-testid="tool-detail-hero-cta">
            {/* G2-style: outlined secondary action first, then the filled primary action */}
            {vendorCtas.slice(1, 2).map((c, i) =>
              isFormCta(c) ? (
                <button
                  key={`sec-${i}`}
                  type="button"
                  onClick={() => openDemo(c)}
                  className="tv-btn justify-center !px-6 !py-3 !text-[14px] font-bold"
                  data-testid={`tool-detail-cta-${i + 1}`}
                >
                  {c.label || "Request a demo"}
                </button>
              ) : (
                <a
                  key={`sec-${i}`}
                  href={c.url}
                  target={c.url.startsWith("mailto:") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  onClick={() => recordLaunch(tool.id)}
                  className="tv-btn justify-center !px-6 !py-3 !text-[14px] font-bold"
                  data-testid={`tool-detail-cta-${i + 1}`}
                >
                  {c.label || `visit ${domainOf(tool.url)}`}
                </a>
              )
            )}
            {isFormCta(primaryCta) ? (
              <button
                type="button"
                onClick={() => openDemo(primaryCta)}
                className="tv-btn tv-btn-primary justify-center !px-6 !py-3 !text-[14px] font-bold"
                data-testid="tool-detail-vendor-cta"
              >
                [↗] {primaryCta.label || "Request a demo"}
              </button>
            ) : (
              <a
                href={primaryCta.url}
                target={primaryCta.url.startsWith("mailto:") ? undefined : "_blank"}
                rel="noopener noreferrer"
                onClick={() => recordLaunch(tool.id)}
                className="tv-btn tv-btn-primary justify-center !px-6 !py-3 !text-[14px] font-bold"
                data-testid="tool-detail-vendor-cta"
              >
                [↗] {primaryCta.label || `visit ${domainOf(tool.url)}`}
              </a>
            )}
          </div>
        )}

        {hasVendor && <VendorSpotlight vendor={tool.vendor} title={tool.title} />}

        {!hasVendor && (
          <p
            className="mt-4 text-[14px] text-[#222] leading-relaxed"
            data-testid="tool-detail-description"
          >
            {tool.overview || tool.description}
          </p>
        )}

        {tool.best_for && (
          <div className="mt-5 border border-black p-3" data-testid="tool-detail-best-for">
            <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">
              best for
            </div>
            <div className="text-[13px] text-[#000] leading-relaxed">
              {tool.best_for}
            </div>
          </div>
        )}

        {Array.isArray(tool.workflow_position) && tool.workflow_position.length > 0 && (
          <div className="mt-5" data-testid="tool-detail-workflow">
            <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">
              typical workflow position
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[12px]">
              {tool.workflow_position.map((w, i) => (
                <span key={w} className="flex items-center gap-1">
                  {i > 0 && <span className="text-[#bbb]">→</span>}
                  <span className="border border-black px-2 py-0.5 bg-[#FBF8EE]">
                    {w.toLowerCase()}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {tool.prompt_text && <PromptBlock promptText={tool.prompt_text} />}

        <ToolCTA tool={tool} />

        <div className="mt-6 border-t border-[#eee] pt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-[13px]">
          <KV label="key feature" value={tool.key_feature} />
          <KV label="asset class" value={tool.asset_class} link={`/c/${slugifyClient(tool.asset_class)}`} />
          <KV
            label="function"
            value={
              tool.category_2
                ? `${tool.category}, ${tool.category_2}`
                : tool.category
            }
            link={`/f/${slugifyClient(tool.category)}`}
          />
          <KV label="pricing" value={price} />
          {tool.pricing_model && (
            <KV label="model" value={tool.pricing_model.replace("_", " ")} />
          )}
          <KV
            label="added"
            value={timeAgo(tool.approved_at || tool.submitted_at)}
          />
          <KV label="upvotes" value={String(tool.upvotes || 0)} />
          {Array.isArray(tool.delivery) && tool.delivery.length > 0 && (
            <KV
              label="via"
              value={tool.delivery
                .map((d) => {
                  if (d === "chatgpt_gpt") return "GPT";
                  if (d === "self_host") return "self-host";
                  return d.replace(/_/g, " ");
                })
                .join(", ")}
            />
          )}
          {Array.isArray(tool.instruments) && tool.instruments.length > 0 && (
            <KV label="instruments" value={tool.instruments.join(", ")} />
          )}
          {tool.is_open_source && (
            <KV label="source" value="open source" />
          )}
          {Array.isArray(tool.regions) && tool.regions.length > 0 && (
            <KV label="regions" value={tool.regions.join(", ")} />
          )}
          {Array.isArray(tool.sectors) && tool.sectors.length > 0 && (
            <KV
              label="sector"
              value={tool.sectors.join(", ")}
              link={tool.sectors.length === 1 ? `/sector/${slugifyClient(tool.sectors[0])}` : undefined}
            />
          )}
          {tool.audience && (
            <KV
              label="audience"
              value={
                tool.audience === "pro" && tool.pro_segment
                  ? `pro · ${tool.pro_segment.replace("_", " ")}`
                  : tool.audience
              }
            />
          )}
          {Array.isArray(tool.user_base) && tool.user_base.length > 0 && (
            <KV
              label="user base"
              value={tool.user_base.join(", ")}
              link={tool.user_base.length === 1 ? `/for/${slugifyClient(tool.user_base[0])}` : undefined}
            />
          )}
          {tool.affiliated && (
            <KV
              label="disclosure"
              value="affiliated with TerminalValue.io"
              link="/terms#affiliate-disclosure"
            />
          )}
        </div>

        <PricingPlans detail={tool.pricing_detail} checkedAt={tool.pricing_checked_at} />

        <div className="mt-8 flex flex-wrap gap-2">
          <AddToTerminalButton slug={tool.slug} title={tool.title} testid="tool-detail-add-terminal" />
          {!hasVendor && tool.vendor?.cta_url && (
            <a
              href={tool.vendor.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => recordLaunch(tool.id)}
              className="tv-btn tv-btn-primary"
              data-testid="tool-detail-vendor-cta"
            >
              [↗] {tool.vendor.cta_label || `visit ${domainOf(tool.url)}`}
            </a>
          )}
          {!tool.prompt_text && !tool.vendor?.cta_url && (
            <a
              href={outboundHref(tool, "tool")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => recordLaunch(tool.id)}
              className="tv-btn tv-btn-primary"
              data-testid="tool-detail-visit"
            >
              [↗] visit {domainOf(tool.url)}
            </a>
          )}
          <Link to="/" className="tv-btn" data-testid="tool-detail-back">
            [← back to index]
          </Link>
          <Link
            to={`/contact?subject=correction&tool_url=${encodeURIComponent(
              tool.url,
            )}`}
            className="tv-btn"
            data-testid="tool-detail-correct"
          >
            [!] suggest a correction
          </Link>
        </div>

        {/* Share row — brutalist text links, no icons. */}
        <div
          className="mt-6 pt-4 border-t border-[#eee] text-[12px] text-[#666] flex flex-wrap items-baseline gap-x-3 gap-y-1"
          data-testid="share-row"
        >
          <span className="text-[#888]">[share]</span>
          <button
            type="button"
            onClick={async () => {
              const url = shareUrl(`${window.location.origin}/tool/${tool.slug}`);
              try {
                await navigator.clipboard.writeText(url);
                toast.success("link copied");
              } catch {
                toast.error("could not copy");
              }
            }}
            className="tv-link"
            data-testid="share-copy"
            title="Copy link to clipboard"
          >
            copy link
          </button>
          <span className="text-[#bbb]">·</span>
          <a
            href={`https://x.com/intent/post?text=${encodeURIComponent(
              `${tool.title} — ${tool.key_feature}`,
            )}&url=${encodeURIComponent(
              shareUrl(`${window.location.origin}/tool/${tool.slug}`),
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tv-link"
            data-testid="share-twitter"
          >
            x
          </a>
          <span className="text-[#bbb]">·</span>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
              shareUrl(`${window.location.origin}/tool/${tool.slug}`),
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tv-link"
            data-testid="share-linkedin"
          >
            linkedin
          </a>
          <span className="text-[#bbb]">·</span>
          <a
            href={`mailto:?subject=${encodeURIComponent(
              `${tool.title} — TerminalValue.io`,
            )}&body=${encodeURIComponent(
              `${tool.description}\n\n${shareUrl(`${window.location.origin}/tool/${tool.slug}`)}`,
            )}`}
            className="tv-link"
            data-testid="share-email"
          >
            email
          </a>
        </div>
        <EmbedBadgeCallout tool={tool} />
        <RelatedTools slug={tool.slug} title={tool.title} />
        {hasVendor && primaryCta && <div className="h-16" aria-hidden="true" />}
      </main>
      <Footer />
      {hasVendor && primaryCta && (
        <StickyCTA
          title={tool.title}
          cta={primaryCta}
          fallbackLabel={`visit ${domainOf(tool.url)}`}
          onLaunch={() => recordLaunch(tool.id)}
          onOpenForm={() => openDemo(primaryCta)}
        />
      )}
      <DemoRequestModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        slug={tool.slug}
        toolTitle={tool.title}
        label={demoLabel}
      />
    </div>
  );
}

function StickyCTA({ title, cta, fallbackLabel, onLaunch, onOpenForm }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const form = cta.type === "demo" || cta.type === "meeting";
  const label = cta.label || fallbackLabel;
  return (
    <div
      className={
        "fixed bottom-0 left-0 right-0 z-40 border-t border-black bg-[#FBF8EE] px-4 py-2 flex items-center gap-3 transition-transform duration-200 " +
        (show ? "translate-y-0" : "translate-y-full pointer-events-none")
      }
      data-testid="tool-detail-sticky-cta"
      aria-hidden={!show}
    >
      <span className="text-[13px] font-bold truncate flex-1">{title}</span>
      {form ? (
        <button
          type="button"
          onClick={onOpenForm}
          tabIndex={show ? 0 : -1}
          className="tv-darklink inline-flex items-center gap-2 bg-black text-white border border-black px-4 py-2 text-[13px] font-bold no-underline hover:bg-[#222] transition-colors whitespace-nowrap"
          data-testid="tool-detail-sticky-btn"
        >
          [↗] {label}
        </button>
      ) : (
        <a
          href={cta.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onLaunch}
          tabIndex={show ? 0 : -1}
          className="tv-darklink inline-flex items-center gap-2 bg-black text-white border border-black px-4 py-2 text-[13px] font-bold no-underline hover:bg-[#222] transition-colors whitespace-nowrap"
          data-testid="tool-detail-sticky-btn"
        >
          [↗] {label}
        </a>
      )}
    </div>
  );
}

function EmbedBadgeCallout({ tool }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 pt-4 border-t border-[#eee]" data-testid="embed-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="tv-link text-[12px]"
        data-testid="embed-badge-toggle"
        title="Embed a live upvote badge on your own site"
      >
        {open ? "[−] embed your badge" : "[+] own this tool? embed your badge"}
      </button>
      {open && (
        <div className="mt-3">
          <BadgeEmbed slug={tool.slug} upvotes={tool.upvotes || 0} />
          <p className="text-[11px] text-[#888] mt-3 m-0">
            Not your tool or need it updated?{" "}
            <Link to="/contact" className="tv-link" data-testid="embed-badge-contact">
              contact us
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function PricingPlans({ detail, checkedAt }) {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  let verified = "";
  if (checkedAt) {
    const d = new Date(checkedAt);
    if (!isNaN(d)) {
      verified = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
  }
  return (
    <div className="mt-6 pt-4 border-t border-[#eee]" data-testid="tool-detail-pricing-plans">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-[#888]">pricing</div>
        {verified && (
          <div className="text-[10px] text-[#aaa]" data-testid="tool-detail-pricing-verified">
            verified {verified}
          </div>
        )}
      </div>
      <div className="text-[13px]">
        {detail.map((p, i) => (
          <div
            key={i}
            className="flex items-baseline justify-between gap-4 py-1 border-b border-[#f4f4f4] last:border-0"
            data-testid={`tool-detail-plan-${i}`}
          >
            <span className="text-[#000]">{p.plan || "—"}</span>
            <span className="text-[#444] font-bold whitespace-nowrap">{p.price || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KV({ label, value, link }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[#888] text-[11px] uppercase tracking-wide">
        {label}
      </span>
      {link ? (
        <Link to={link} className="tv-link text-[13px]">
          {value}
        </Link>
      ) : (
        <span className="text-[13px] text-[#000]">{value}</span>
      )}
    </div>
  );
}

// Local slugifier; mirrors backend behavior. Imported here to avoid circular deps.
function slugifyClient(s) {
  return (s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function DemoRequestModal({ open, onClose, slug, toolTitle, label }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", hp: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", company: "", message: "", hp: "" });
      setDone(false);
      setSending(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Add your name and work email");
      return;
    }
    setSending(true);
    try {
      await submitVendorLead({ slug, ...form });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      data-testid="demo-modal-overlay"
    >
      <div
        className="w-full max-w-md bg-[#FBF8EE] border-2 border-black shadow-[6px_6px_0_0_#000] p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-[16px] font-bold m-0" data-testid="demo-modal-title">
            {label || "Request a demo"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="tv-link text-[13px] font-bold"
            data-testid="demo-modal-close"
            aria-label="Close"
          >
            [×]
          </button>
        </div>

        {done ? (
          <div data-testid="demo-modal-success">
            <p className="text-[13px] leading-relaxed m-0 mt-3">
              Sent. The {toolTitle} team will reach out to the email you gave.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="tv-btn tv-btn-primary !px-5 !py-2.5 !text-[13px] font-bold mt-4"
              data-testid="demo-modal-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-3">
            <p className="text-[12px] text-[#666] m-0 mb-4">
              Tell {toolTitle} what you'd like to see. You stay on TerminalValue — they get your request by email.
            </p>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp}
              onChange={set("hp")}
              className="hidden"
              aria-hidden="true"
            />
            <label className="block text-[11px] uppercase tracking-wide text-[#888] mb-1">name</label>
            <input
              value={form.name}
              onChange={set("name")}
              className="w-full border border-black bg-white px-3 py-2 text-[13px] mb-3 outline-none focus:ring-2 focus:ring-black"
              data-testid="demo-modal-name"
              maxLength={120}
            />
            <label className="block text-[11px] uppercase tracking-wide text-[#888] mb-1">work email</label>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              className="w-full border border-black bg-white px-3 py-2 text-[13px] mb-3 outline-none focus:ring-2 focus:ring-black"
              data-testid="demo-modal-email"
              maxLength={200}
            />
            <label className="block text-[11px] uppercase tracking-wide text-[#888] mb-1">company <span className="normal-case text-[#bbb]">(optional)</span></label>
            <input
              value={form.company}
              onChange={set("company")}
              className="w-full border border-black bg-white px-3 py-2 text-[13px] mb-3 outline-none focus:ring-2 focus:ring-black"
              data-testid="demo-modal-company"
              maxLength={160}
            />
            <label className="block text-[11px] uppercase tracking-wide text-[#888] mb-1">what do you want to see? <span className="normal-case text-[#bbb]">(optional)</span></label>
            <textarea
              value={form.message}
              onChange={set("message")}
              rows={3}
              className="w-full border border-black bg-white px-3 py-2 text-[13px] mb-4 outline-none focus:ring-2 focus:ring-black resize-none"
              data-testid="demo-modal-message"
              maxLength={1500}
            />
            <button
              type="submit"
              disabled={sending}
              className="tv-btn tv-btn-primary justify-center w-full !px-5 !py-3 !text-[13px] font-bold disabled:opacity-60"
              data-testid="demo-modal-submit"
            >
              {sending ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
        <div className="mt-5 pt-3 border-t border-[#e5e0d0] text-center" data-testid="demo-modal-poweredby">
          <a
            href="/"
            className="text-[11px] text-[#999] no-underline hover:text-[#555] transition-colors"
          >
            ⚡ Powered by <span className="font-bold">TerminalValue.io</span>
          </a>
        </div>
      </div>
    </div>
  );
}
